<template>
  <div>
    <DataTable
      :value="tableData"
      striped-rows
      class="p-datatable-sm"
      resizable-columns
      scrollable
    >
      <Column header-class="flex px-0" field="property" header="Correlation">
        <!-- Allow to show status of other stages -->
        <!-- <template #header>
          <Dropdown
            v-model="stage"
            :options="stages"
            option-label="name"
            option-value="location"
          />
        </template> -->
      </Column>
      <Column
        v-for="field in Object.keys(tableData?.[0] || {}).slice(
          1
        ) as (keyof typeof corrAbbrevs)[]"
        :key="field"
        :field="field"
        body-class="text-center"
      >
        <template #header>
          <span v-tooltip.bottom="humanize(field)">
            {{ corrAbbrevs[field] }}
          </span>
        </template>
        <template #body="{ data }">
          <icon size="xl" :icon="data[field] || ['fas', 'horizontal-rule']" />
        </template>
      </Column>
      <template #empty> No Relevant Checks </template>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { scenarioValidatorKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import {
  corrAbbrevs,
  // correlationCheckStageNames,
  type StageNames
} from "@/composables/validation/rules/correlationChecks";
import { CORRELATIONS_FIELDS } from "@/composables/validation/validatorConstants";
import {
  useScenarioValidator,
  type ValidationRule
} from "@/composables/validation/scenarioValidator";
import { humanize } from "@/utils/utils";
import { computed, inject, ref, watchEffect } from "vue";
import type { ScenarioMap } from "../types";

const emit = defineEmits<{ (e: "update:isPassing", value: boolean): void }>();
const props = defineProps<{ scenario?: ScenarioMap[string] }>();

const scenarioValidator = inject(
  scenarioValidatorKey,
  props.scenario?.calcs &&
    useScenarioValidator(props.scenario, props.scenario.calcs)
);

if (!scenarioValidator)
  throw new Error("Scenario Not Found in CorrelationChecksTable");

const { correlationRules } = scenarioValidator;

const correlationTests = Object.keys(corrAbbrevs);
const correlationFieldNames = Object.values(CORRELATIONS_FIELDS).flat();

type TableRow = {
  [k: string]: string | [string, string];
};

const stage = ref<StageNames>("intake");
// const stages = correlationCheckStageNames.map(location => ({
//   location,
//   name: `@ ${humanize(location)}`
// }));

const isAtStage = (name: string) => {
  if (!name.endsWith(stage.value)) return false;

  // catch false positives for pump_intake
  if (stage.value === "intake" && name.endsWith("pump_intake")) return false;
  return true;
};

const getStatusIcon = (rule: ValidationRule): [string, string] => {
  if (!rule.isActive) return ["fas", "horizontal-rule"];
  if (rule.isInvalid) return ["fas", "square-x"];
  return ["fas", "square-check"];
};

const tableData = computed<TableRow[]>(() => {
  const relevantRules = correlationRules.value.filter(rule =>
    isAtStage(rule.name)
  );

  const ruleMap = new Map<string, TableRow>();

  for (const field of correlationTests) {
    for (const rule of relevantRules) {
      if (!rule.name.includes(`${field}_${stage.value}`)) continue;
      const rawProperty = rule.name.slice(
        0,
        rule.name.length - stage.value.length - field.length - 2
      ) as (typeof correlationFieldNames)[number];

      if (!correlationFieldNames.includes(rawProperty)) continue;

      // change string name in chart for one off
      const property =
        rawProperty === "solution_gor" ? "solution_gas" : rawProperty;

      const row = ruleMap.get(property);
      if (!row) {
        ruleMap.set(property, {
          property: humanize(property),
          [field]: getStatusIcon(rule)
        });
      } else {
        row[field] = getStatusIcon(rule);
      }
    }
  }

  return [...ruleMap.values()];
});

// tell parent if any checks are currently failing
watchEffect(() => {
  let isPassing = true;
  for (const cols of tableData.value) {
    for (const field of Object.values(cols)) {
      if (typeof field === "string") continue;
      if (field[1] === "square-x") isPassing = false;
    }
  }
  emit("update:isPassing", isPassing);
});
</script>

<style scoped>
:deep(.p-column-header-content) {
  justify-content: center;
}
:deep(.fa-square-check) {
  color: var(--success-500);
}
:deep(.fa-square-x) {
  color: var(--danger-500);
}
:deep(.p-datatable .p-datatable-thead > tr > th) {
  padding: 0.5rem 0 !important;
}
</style>
